import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DocumentUpload } from '../components/DocumentUpload';
import { SLAIndicator } from '../components/SLAIndicator';
import ComplianceCycleComponent from '../components/ComplianceCycleComponent';
import ExecutiveDossierButton from '../components/ExecutiveDossierButton';
import RiskIntelligenceBlock from '../components/RiskIntelligenceBlock';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Clock, AlertTriangle, Scale, Building2, FileText,
  Plus, Upload, CheckCircle, XCircle, Loader2, ChevronRight, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  InternalExecutionTab,
  ForwardsTab,
  FormalResponsibleSection,
  LegalOpinionsTab,
  AcknowledgementsSection,
  RiskProfileSection,
  CostImpactSection
} from '../components/ActExtendedFeatures';
import { ActManagementPanel } from '../components/ActManagementFeatures';

const SEVERITIES = {
  LOW: { label: 'Baixa', color: 'bg-green-100 text-green-800' },
  MEDIUM: { label: 'Média', color: 'bg-yellow-100 text-yellow-800' },
  HIGH: { label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  CRITICAL: { label: 'Crítica', color: 'bg-red-100 text-red-800' },
};

const STATUSES = {
  RECEBIDO: { label: 'Recebido', color: 'bg-blue-100 text-blue-800', next: 'CLASSIFICADO' },
  CLASSIFICADO: { label: 'Classificado', color: 'bg-indigo-100 text-blue-800', next: 'PLANEJADO' },
  PLANEJADO: { label: 'Planejado', color: 'bg-blue-100 text-blue-800', next: 'EM_EXECUCAO' },
  EM_EXECUCAO: { label: 'Em Execução', color: 'bg-yellow-100 text-yellow-800', next: 'AGUARDANDO_EVIDENCIA' },
  AGUARDANDO_EVIDENCIA: { label: 'Aguard. Evidência', color: 'bg-orange-100 text-orange-800', next: 'EM_REVISAO' },
  EM_REVISAO: { label: 'Em Revisão', color: 'bg-pink-100 text-pink-800', next: 'RESPONDIDO' },
  RESPONDIDO: { label: 'Respondido', color: 'bg-cyan-100 text-cyan-800', next: 'ENCERRADO' },
  ENCERRADO: { label: 'Encerrado', color: 'bg-green-100 text-green-800', next: 'ARQUIVADO' },
  ARQUIVADO: { label: 'Arquivado', color: 'bg-slate-100 text-slate-800', next: null },
};

const PLAN_STATUSES = {
  OPEN: { label: 'Aberto', color: 'bg-blue-100 text-blue-800' },
  WAITING_EVIDENCE: { label: 'Aguard. Evidência', color: 'bg-orange-100 text-orange-800' },
  APPROVED: { label: 'Aprovado', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Rejeitado', color: 'bg-red-100 text-red-800' },
};

const EVIDENCE_STATUSES = {
  PENDING: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
  ACCEPTED: { label: 'Aceita', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Rejeitada', color: 'bg-red-100 text-red-800' },
};

export const ActDetailPage = () => {
  const { actId } = useParams();
  const navigate = useNavigate();
  const { api, hasPermission } = useAuth();

  const [act, setAct] = useState(null);
  const [actionPlans, setActionPlans] = useState([]);
  const [responses, setResponses] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  // Dialogs
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [showEvidenceSheet, setShowEvidenceSheet] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planEvidence, setPlanEvidence] = useState([]);

  // Form states
  const [newPlan, setNewPlan] = useState({
    action_description: '',
    responsible_unit: '',
    responsible_user: '',
    deadline: '',
    evidence_required: true
  });
  const [newResponse, setNewResponse] = useState({
    technical_response: '',
    executive_summary: ''
  });
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/acts/${actId}/documents`);
      setDocuments(res.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [actRes, plansRes, responsesRes, docsRes] = await Promise.all([
          api.get(`/acts/${actId}`),
          api.get(`/acts/${actId}/plans`),
          api.get(`/acts/${actId}/responses`),
          api.get(`/acts/${actId}/documents`)
        ]);
        setAct(actRes.data);
        setActionPlans(plansRes.data);
        setResponses(responsesRes.data);
        setDocuments(docsRes.data);
      } catch (error) {
        console.error('Error fetching act details:', error);
        toast.error('Erro ao carregar detalhes do ato');
        navigate('/acts');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [actId, api, navigate]);

  const handleTransition = async () => {
    if (!act || !STATUSES[act.status].next) return;

    setTransitioning(true);
    try {
      await api.put(`/acts/${actId}/transition?new_status=${STATUSES[act.status].next}`);
      const actRes = await api.get(`/acts/${actId}`);
      setAct(actRes.data);
      toast.success(`Status alterado para ${STATUSES[STATUSES[act.status].next].label}`);
    } catch (error) {
      console.error('Error transitioning act:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    } finally {
      setTransitioning(false);
    }
  };

  const handleCreatePlan = async () => {
    try {
      const response = await api.post(`/acts/${actId}/plans`, {
        ...newPlan,
        deadline: new Date(newPlan.deadline).toISOString()
      });
      setActionPlans([...actionPlans, response.data]);
      setShowPlanDialog(false);
      setNewPlan({
        action_description: '',
        responsible_unit: '',
        responsible_user: '',
        deadline: '',
        evidence_required: true
      });
      toast.success('Plano de ação criado');
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Erro ao criar plano de ação');
    }
  };

  const handleCreateResponse = async () => {
    try {
      const response = await api.post(`/acts/${actId}/responses`, newResponse);
      setResponses([...responses, response.data]);
      setShowResponseDialog(false);
      setNewResponse({ technical_response: '', executive_summary: '' });
      toast.success('Resposta criada');
    } catch (error) {
      console.error('Error creating response:', error);
      toast.error('Erro ao criar resposta');
    }
  };

  const handleViewEvidence = async (plan) => {
    setSelectedPlan(plan);
    try {
      const response = await api.get(`/plans/${plan.plan_id}/evidence`);
      setPlanEvidence(response.data);
    } catch (error) {
      console.error('Error fetching evidence:', error);
      setPlanEvidence([]);
    }
    setShowEvidenceSheet(true);
  };

  const handleUploadEvidence = async (e) => {
    if (!selectedPlan || !e.target.files?.[0]) return;

    setUploadingEvidence(true);
    const formData = new FormData();
    formData.append('file', e.target.files[0]);

    try {
      await api.post(`/plans/${selectedPlan.plan_id}/evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const response = await api.get(`/plans/${selectedPlan.plan_id}/evidence`);
      setPlanEvidence(response.data);
      const plansRes = await api.get(`/acts/${actId}/plans`);
      setActionPlans(plansRes.data);
      toast.success('Evidência enviada com sucesso');
    } catch (error) {
      console.error('Error uploading evidence:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar evidência');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleValidateEvidence = async (evidenceId, status) => {
    try {
      await api.put(`/evidence/${evidenceId}/validate?status=${status}`);
      const response = await api.get(`/plans/${selectedPlan.plan_id}/evidence`);
      setPlanEvidence(response.data);
      const plansRes = await api.get(`/acts/${actId}/plans`);
      setActionPlans(plansRes.data);
      toast.success(`Evidência ${status === 'ACCEPTED' ? 'aprovada' : 'rejeitada'}`);
    } catch (error) {
      console.error('Error validating evidence:', error);
      toast.error('Erro ao validar evidência');
    }
  };

  const isOverdue = act?.deadline && new Date(act.deadline) < new Date() && 
    act.status !== 'ENCERRADO' && act.status !== 'ARQUIVADO';

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-96 lg:col-span-2" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!act) return null;

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="act-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/acts')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900">{act.control_body}</h1>
                {act.is_judicialized && <Scale className="w-5 h-5 text-red-500" title="Judicializado" />}
                {act.risk_level === 'CRITICAL' && <AlertTriangle className="w-5 h-5 text-red-500" title="Risco Crítico" />}
              </div>
              <div className="flex items-center gap-3">
                {act.act_number && (
                  <p className="text-slate-700 font-semibold">{act.act_number}</p>
                )}
                <p className="text-slate-400 font-mono text-sm">{act.act_id}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={STATUSES[act.status]?.color}>{STATUSES[act.status]?.label}</Badge>
            <ExecutiveDossierButton actId={act.act_id} />
            {hasPermission('edit') && STATUSES[act.status]?.next && (
              <Button
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={handleTransition}
                disabled={transitioning}
                data-testid="transition-btn"
              >
                {transitioning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Avançar para {STATUSES[STATUSES[act.status].next]?.label}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Card */}
            <Card data-testid="act-info-card">
              <CardHeader>
                <CardTitle>Informações do Ato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</p>
                    <p className="text-sm font-medium text-slate-900">{act.type.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Área Temática</p>
                    <p className="text-sm font-medium text-slate-900">{act.thematic_area}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Severidade</p>
                    <Badge className={SEVERITIES[act.severity]?.color}>{SEVERITIES[act.severity]?.label}</Badge>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Nível de Risco</p>
                    <Badge className={SEVERITIES[act.risk_level]?.color}>{SEVERITIES[act.risk_level]?.label}</Badge>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Prazo</p>
                    <div className={`flex items-center gap-1 text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-slate-900'}`}>
                      {isOverdue && <Clock className="w-4 h-4" />}
                      {format(new Date(act.deadline), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Unidade Responsável</p>
                    <p className="text-sm font-medium text-slate-900">{act.responsible_unit || 'Não atribuído'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Escopo</p>
                  <p className="text-sm text-slate-700">{act.scope}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Descrição</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{act.description}</p>
                </div>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                  {act.is_recurring && (
                    <Badge variant="outline" className="text-orange-600 border-orange-200">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Reincidente
                    </Badge>
                  )}
                  {act.is_judicialized && (
                    <Badge variant="outline" className="text-red-600 border-red-200">
                      <Scale className="w-3 h-3 mr-1" />
                      Judicializado
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tabs: Plans, Evidence, Response */}
            <Card>
              <Tabs defaultValue="plans">
                <CardHeader className="pb-0">
                  <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="plans" data-testid="tab-plans">Plano de Ação</TabsTrigger>
                    <TabsTrigger value="compliance" data-testid="tab-compliance">Ciclo de Cumprimento</TabsTrigger>
                    <TabsTrigger value="internal" data-testid="tab-internal">Execução Interna</TabsTrigger>
                    <TabsTrigger value="forwards" data-testid="tab-forwards">Encaminhamentos</TabsTrigger>
                    <TabsTrigger value="legal" data-testid="tab-legal">Parecer Jurídico</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="tab-documents">Documentos</TabsTrigger>
                    <TabsTrigger value="response" data-testid="tab-response">Resposta</TabsTrigger>
                    <TabsTrigger value="management" data-testid="tab-management">Gestão</TabsTrigger>
                  </TabsList>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Action Plans */}
                  <TabsContent value="plans" className="mt-0">
                    {hasPermission('edit') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mb-4"
                        onClick={() => setShowPlanDialog(true)}
                        data-testid="add-plan-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Ação
                      </Button>
                    )}
                    {actionPlans.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        Nenhum plano de ação cadastrado
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {actionPlans.map((plan) => (
                          <div
                            key={plan.plan_id}
                            className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                            data-testid={`plan-${plan.plan_id}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">{plan.action_description}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Building2 className="w-3 h-3" />
                                    {plan.responsible_unit}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {format(new Date(plan.deadline), 'dd/MM/yyyy')}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={PLAN_STATUSES[plan.status]?.color}>
                                  {PLAN_STATUSES[plan.status]?.label}
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewEvidence(plan)}
                                  data-testid={`view-evidence-${plan.plan_id}`}
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Evidências
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Compliance Cycle - Ciclo de Cumprimento */}
                  <TabsContent value="compliance" className="mt-0">
                    <ComplianceCycleComponent 
                      actId={actId} 
                      act={act} 
                      onUpdate={async () => {
                        const actRes = await api.get(`/acts/${actId}`);
                        setAct(actRes.data);
                      }} 
                    />
                  </TabsContent>

                  {/* Internal Execution */}
                  <TabsContent value="internal" className="mt-0">
                    <InternalExecutionTab actId={actId} />
                  </TabsContent>

                  {/* Forwards */}
                  <TabsContent value="forwards" className="mt-0">
                    <ForwardsTab actId={actId} />
                  </TabsContent>

                  {/* Legal Opinions */}
                  <TabsContent value="legal" className="mt-0">
                    <LegalOpinionsTab actId={actId} />
                  </TabsContent>

                  {/* Documents */}
                  <TabsContent value="documents" className="mt-0">
                    <DocumentUpload 
                      actId={actId}
                      documents={documents}
                      onUploadComplete={() => fetchDocuments()}
                      onDeleteComplete={() => fetchDocuments()}
                    />
                  </TabsContent>

                  {/* Management - Prorrogação e Delegação */}
                  <TabsContent value="management" className="mt-0">
                    <ActManagementPanel 
                      actId={actId}
                      act={act}
                      onUpdate={async (updatedAct) => {
                        if (updatedAct) {
                          setAct(updatedAct);
                        } else {
                          const actRes = await api.get(`/acts/${actId}`);
                          setAct(actRes.data);
                        }
                      }}
                    />
                  </TabsContent>

                  {/* Response */}
                  <TabsContent value="response" className="mt-0">
                    {hasPermission('respond') && responses.length === 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mb-4"
                        onClick={() => setShowResponseDialog(true)}
                        data-testid="add-response-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Criar Resposta
                      </Button>
                    )}
                    {responses.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        Nenhuma resposta cadastrada
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {responses.map((resp) => (
                          <div key={resp.response_id} className="border border-slate-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-4">
                              <Badge className={
                                resp.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                resp.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-800' :
                                'bg-slate-100 text-slate-800'
                              }>
                                {resp.status === 'APPROVED' ? 'Aprovada' : 
                                 resp.status === 'SUBMITTED' ? 'Enviada' : 'Rascunho'}
                              </Badge>
                              <span className="text-xs text-slate-500 font-mono">
                                {format(new Date(resp.created_at), 'dd/MM/yyyy HH:mm')}
                              </span>
                            </div>
                            <div className="mb-4">
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                                Resumo Executivo
                              </p>
                              <p className="text-sm text-slate-700">{resp.executive_summary}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                                Resposta Técnica
                              </p>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{resp.technical_response}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* SLA Indicator */}
            <SLAIndicator 
              actId={act.act_id}
              actType={act.type}
              controlBody={act.control_body}
              deadline={act.deadline}
            />

            {/* Inteligência de Risco (Fase 3) */}
            <RiskIntelligenceBlock api={api} actId={act.act_id} />

            {/* Timeline */}
            <Card data-testid="timeline-card">
              <CardHeader>
                <CardTitle className="text-lg">Histórico</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-dot" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-slate-900">Ato criado</p>
                      <p className="text-xs text-slate-500 font-mono">
                        {format(new Date(act.created_at), 'dd/MM/yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                  {act.updated_at && (
                    <div className="timeline-item">
                      <div className="timeline-dot" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-slate-900">Última atualização</p>
                        <p className="text-xs text-slate-500 font-mono">
                          {format(new Date(act.updated_at), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  )}
                  {act.closed_at && (
                    <div className="timeline-item">
                      <div className="timeline-dot" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-slate-900">Encerrado</p>
                        <p className="text-xs text-slate-500 font-mono">
                          {format(new Date(act.closed_at), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Metadados</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Criado por</p>
                  <p className="text-sm font-mono text-slate-700">{act.created_by}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Tenant</p>
                  <p className="text-sm font-mono text-slate-700">{act.tenant_id}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">ID do Ato</p>
                  <p className="text-sm font-mono text-slate-700">{act.act_id}</p>
                </div>
              </CardContent>
            </Card>

            {/* Risk Profile Section */}
            <RiskProfileSection actId={actId} />

            {/* Cost Impact Section */}
            <CostImpactSection actId={actId} />

            {/* Formal Responsible Section */}
            <FormalResponsibleSection actId={actId} />

            {/* Acknowledgements Section */}
            <AcknowledgementsSection actId={actId} />
          </div>
        </div>

        {/* Create Plan Dialog */}
        <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo Plano de Ação</DialogTitle>
              <DialogDescription>Adicione uma ação ao plano de tratamento</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Descrição da Ação</Label>
                <Textarea
                  value={newPlan.action_description}
                  onChange={(e) => setNewPlan({ ...newPlan, action_description: e.target.value })}
                  placeholder="Descreva a ação a ser executada"
                  data-testid="plan-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unidade Responsável</Label>
                  <Input
                    value={newPlan.responsible_unit}
                    onChange={(e) => setNewPlan({ ...newPlan, responsible_unit: e.target.value })}
                    placeholder="Ex: Secretaria de Saúde"
                    data-testid="plan-unit-input"
                  />
                </div>
                <div>
                  <Label>Prazo</Label>
                  <Input
                    type="date"
                    value={newPlan.deadline}
                    onChange={(e) => setNewPlan({ ...newPlan, deadline: e.target.value })}
                    data-testid="plan-deadline-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancelar</Button>
              <Button
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={handleCreatePlan}
                disabled={!newPlan.action_description || !newPlan.responsible_unit || !newPlan.deadline}
                data-testid="save-plan-btn"
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Response Dialog */}
        <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nova Resposta</DialogTitle>
              <DialogDescription>Elabore a resposta técnica e o resumo executivo</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Resumo Executivo</Label>
                <Textarea
                  value={newResponse.executive_summary}
                  onChange={(e) => setNewResponse({ ...newResponse, executive_summary: e.target.value })}
                  placeholder="Resumo para leitura rápida dos gestores"
                  rows={3}
                  data-testid="response-summary-input"
                />
              </div>
              <div>
                <Label>Resposta Técnica</Label>
                <Textarea
                  value={newResponse.technical_response}
                  onChange={(e) => setNewResponse({ ...newResponse, technical_response: e.target.value })}
                  placeholder="Resposta técnica detalhada"
                  rows={6}
                  data-testid="response-technical-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResponseDialog(false)}>Cancelar</Button>
              <Button
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={handleCreateResponse}
                disabled={!newResponse.technical_response || !newResponse.executive_summary}
                data-testid="save-response-btn"
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Evidence Sheet */}
        <Sheet open={showEvidenceSheet} onOpenChange={setShowEvidenceSheet}>
          <SheetContent className="w-full sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>Evidências</SheetTitle>
              <SheetDescription>
                {selectedPlan?.action_description}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              {hasPermission('upload_evidence') && (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="evidence-upload"
                    className="hidden"
                    onChange={handleUploadEvidence}
                    disabled={uploadingEvidence}
                  />
                  <label
                    htmlFor="evidence-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    {uploadingEvidence ? (
                      <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-400" />
                    )}
                    <span className="text-sm text-slate-600 mt-2">
                      {uploadingEvidence ? 'Enviando...' : 'Clique para enviar evidência'}
                    </span>
                    <span className="text-xs text-slate-400">Máximo 50MB</span>
                  </label>
                </div>
              )}

              {planEvidence.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Nenhuma evidência enviada</p>
              ) : (
                <div className="space-y-3">
                  {planEvidence.map((ev) => (
                    <div
                      key={ev.evidence_id}
                      className="p-4 border border-slate-200 rounded-lg"
                      data-testid={`evidence-${ev.evidence_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{ev.file_name}</p>
                          <p className="text-xs text-slate-500 font-mono mt-1">
                            Hash: {ev.file_hash.substring(0, 16)}...
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {format(new Date(ev.timestamp), 'dd/MM/yyyy HH:mm')}
                          </p>
                        </div>
                        <Badge className={EVIDENCE_STATUSES[ev.validation_status]?.color}>
                          {EVIDENCE_STATUSES[ev.validation_status]?.label}
                        </Badge>
                      </div>

                      {/* Chain of Custody */}
                      {ev.chain_of_custody && ev.chain_of_custody.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                            Cadeia de Custódia
                          </p>
                          <div className="space-y-2">
                            {ev.chain_of_custody.map((entry, idx) => (
                              <div key={idx} className="chain-item">
                                <div className={`chain-dot ${
                                  entry.action.includes('ACCEPTED') ? 'bg-green-500' :
                                  entry.action.includes('REJECTED') ? 'bg-red-500' :
                                  'bg-blue-500'
                                }`} />
                                <div>
                                  <p className="text-xs text-slate-700">{entry.action}</p>
                                  <p className="text-xs text-slate-500 font-mono">
                                    {entry.user_email} - {format(new Date(entry.timestamp), 'dd/MM HH:mm')}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Validation buttons */}
                      {hasPermission('validate') && ev.validation_status === 'PENDING' && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-green-600 hover:bg-green-50"
                            onClick={() => handleValidateEvidence(ev.evidence_id, 'ACCEPTED')}
                            data-testid={`approve-evidence-${ev.evidence_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-red-600 hover:bg-red-50"
                            onClick={() => handleValidateEvidence(ev.evidence_id, 'REJECTED')}
                            data-testid={`reject-evidence-${ev.evidence_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rejeitar
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
};

export default ActDetailPage;
